import { useState, useCallback } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Custom hook for handling file uploads and Google Drive URL validation
 * Manages file selection, drag-and-drop, and Drive URL validation
 */
export const useFileUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [driveUrl, setDriveUrl] = useState('');
  const [driveFileInfo, setDriveFileInfo] = useState(null);
  const [validatingDriveUrl, setValidatingDriveUrl] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFiles([file]);
      
      // Clear validation error when file is selected
      setValidationErrors(prev => ({ ...prev, fileOrUrl: null }));
    }
  }, []);

  // Validate Google Drive URL
  const validateDriveUrl = useCallback(async (url) => {
    if (!url || !url.trim()) {
      setDriveFileInfo(null);
      setValidationErrors(prev => ({ ...prev, driveUrl: null }));
      return;
    }

    // Basic URL validation
    if (!url.includes('drive.google.com')) {
      setDriveFileInfo(null);
      setValidationErrors(prev => ({ 
        ...prev, 
        driveUrl: 'Please enter a valid Google Drive URL' 
      }));
      return;
    }

    setValidatingDriveUrl(true);
    setValidationErrors(prev => ({ ...prev, driveUrl: null }));

    try {
      const response = await axios.post(`${API_BASE}/drive/validate`, {
        drive_url: url.trim()
      }, {
        timeout: 30000, // 30 seconds timeout
      });

      if (response.data.valid) {
        setDriveFileInfo({
          name: response.data.file_name,
          size: response.data.file_size,
          mimeType: response.data.mime_type
        });
        setValidationErrors(prev => ({ ...prev, driveUrl: null }));
        logger.log('✅ Drive URL validated:', response.data);
      } else {
        setDriveFileInfo(null);
        setValidationErrors(prev => ({ 
          ...prev, 
          driveUrl: response.data.error || 'Invalid Google Drive URL' 
        }));
      }
    } catch (error) {
      logger.error('❌ Drive URL validation error:', error);
      setDriveFileInfo(null);
      setValidationErrors(prev => ({ 
        ...prev, 
        driveUrl: 'Failed to validate URL. Please check the URL and try again.' 
      }));
    } finally {
      setValidatingDriveUrl(false);
    }
  }, []);

  // Validate form
  const validateForm = useCallback((projectName, checkProjectNameExists) => {
    const errors = {};
    
    // Project Name is mandatory
    if (!projectName.trim()) {
      errors.projectName = 'Project name is required';
    } else if (checkProjectNameExists && checkProjectNameExists(projectName.trim())) {
      // Note: We'll allow duplicate names but warn the user
      logger.log(`⚠️ Project name "${projectName}" already exists. Backend will create a unique name.`);
    }
    
    // Either file upload or Google Drive URL is mandatory
    if (!selectedFiles.length && !driveUrl.trim()) {
      errors.fileOrUrl = 'Please select files or enter a Google Drive URL';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedFiles, driveUrl]);

  // Clear all file-related state
  const clearFileState = useCallback(() => {
    setSelectedFiles([]);
    setDriveUrl('');
    setDriveFileInfo(null);
    setValidationErrors({});
  }, []);

  return {
    // State
    selectedFiles,
    driveUrl,
    driveFileInfo,
    validatingDriveUrl,
    validationErrors,
    
    // Setters
    setSelectedFiles,
    setDriveUrl,
    setDriveFileInfo,
    setValidatingDriveUrl,
    setValidationErrors,
    
    // Functions
    onDrop,
    validateDriveUrl,
    validateForm,
    clearFileState,
  };
};

